import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import UserAvatar from '../components/UserAvatar';
import api, { formatDate } from '../utils/api';
import { useUsers } from '../lib/queries';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Camera,
  Ellipsis,
  Layers,
  Mail,
  Pencil,
  Plus,
  Scissors,
  Search,
  Trash2,
  Users as UsersIcon,
  Video,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  admin: 'Admin',
  media_head: 'Media head',
  team_member: 'Team member'
};
const ROLE_ORDER = { admin: 0, media_head: 1, team_member: 2 };
const SPECIALIZATIONS = {
  photo: { label: 'Photography', icon: Camera },
  video: { label: 'Videography', icon: Video },
  editing: { label: 'Editing', icon: Scissors },
  other: { label: 'Other', icon: Layers }
};
const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  role: 'team_member',
  specialization: 'photo'
};

const Users = () => {
  const { user: currentUser } = useAuth();
  const { data: users = [], isPending: loading, isError, error: loadError } = useUsers();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load users', {
        description: loadError?.response?.data?.detail || 'Please try again'
      });
    }
  }, [isError, loadError]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        specialization: user.specialization || 'photo'
      });
    } else {
      setEditingUser(null);
      setFormData(EMPTY_FORM);
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name || !formData.email) {
        toast.error('Please fill in required fields');
        return;
      }

      if (!editingUser && !formData.password) {
        toast.error('Password is required for new users');
        return;
      }

      if (editingUser) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          specialization: formData.role === 'team_member' ? formData.specialization : null
        };
        await api.put(`/users/${editingUser.id}`, updateData);
        toast.success('User updated successfully');
      } else {
        const createData = {
          ...formData,
          specialization: formData.role === 'team_member' ? formData.specialization : null
        };
        await api.post('/users', createData);
        toast.success('User created successfully');
      }

      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save user', {
        description: error.response?.data?.detail || 'Please try again'
      });
    }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/users/${userToDelete.id}`);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      const errorMsg =
        typeof error.response?.data?.detail === 'string'
          ? error.response.data.detail
          : 'Failed to delete user';
      toast.error('Failed to delete user', { description: errorMsg });
    }
  };

  const counts = useMemo(
    () => ({
      all: users.length,
      admin: users.filter((u) => u.role === 'admin').length,
      media_head: users.filter((u) => u.role === 'media_head').length,
      team_member: users.filter((u) => u.role === 'team_member').length
    }),
    [users]
  );
  const TABS = [
    { id: 'all', label: 'Everyone' },
    { id: 'admin', label: 'Admins' },
    { id: 'media_head', label: 'Media heads' },
    { id: 'team_member', label: 'Team members' }
  ];

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users
      .filter(
        (u) =>
          (tab === 'all' || u.role === tab) &&
          (!query ||
            u.name.toLowerCase().includes(query) ||
            u.email.toLowerCase().includes(query))
      )
      .sort(
        (a, b) =>
          (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || a.name.localeCompare(b.name)
      );
  }, [users, tab, search]);

  const hasFilters = search || tab !== 'all';
  const clearFilters = () => {
    setSearch('');
    setTab('all');
  };

  return (
    <Layout>
      <div className="dashboard-page institutions-page">
        <div className="dashboard-heading">
          <div>
            <p className="page-eyebrow">
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <h1>Users</h1>
            <p>The people who run the studio and what they do.</p>
          </div>
          <div className="dashboard-heading-actions">
            <Button data-testid="create-user-button" onClick={() => handleOpenDialog()}>
              <Plus size={17} />
              Add user
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="dashboard-loading" role="status">
            <div className="brand-spinner" />
            <h2>Loading users</h2>
            <p>Fetching your team.</p>
          </div>
        ) : (
          <section className="dashboard-panel inst-panel" aria-labelledby="users-list-title">
            <h2 id="users-list-title" className="sr-only">
              All users
            </h2>
            <div className="events-toolbar-row">
              <div className="tab-bar" role="tablist" aria-label="User role">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={tab === t.id}
                    data-testid={`tab-${t.id}`}
                    className={tab === t.id ? 'is-active' : ''}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label}
                    <span>{counts[t.id]}</span>
                  </button>
                ))}
              </div>
              <div className="toolbar-controls">
                <div className="event-search inst-search">
                  <Search size={18} aria-hidden="true" />
                  <input
                    aria-label="Search users"
                    type="search"
                    placeholder="Search name or email"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button onClick={() => setSearch('')} aria-label="Clear search">
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {filtered.length ? (
              <ul className="people-grid">
                {filtered.map((user) => {
                  const spec = SPECIALIZATIONS[user.specialization];
                  const SpecIcon = spec?.icon;
                  const isSelf = currentUser?.id === user.id;
                  return (
                    <li key={user.id}>
                      <article
                        className="person-card"
                        data-testid={`user-row-${user.id}`}
                        onClick={() => handleOpenDialog(user)}
                      >
                        <div className="person-top">
<UserAvatar id={user.id} name={user.name} size={58} className="person-avatar" />
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="row-menu-trigger"
                                aria-label={`Actions for ${user.name}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Ellipsis size={18} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[150px]">
                              <DropdownMenuItem
                                data-testid={`edit-user-button-${user.id}`}
                                onSelect={() => handleOpenDialog(user)}
                              >
                                <Pencil size={15} />
                                Edit details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`delete-user-button-${user.id}`}
                                className="text-red-600 focus:text-red-700"
                                disabled={isSelf}
                                onSelect={() => {
                                  setUserToDelete(user);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 size={15} />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <h3 className="person-name">
                          {user.name}
                          {isSelf && <span className="you-tag">You</span>}
                        </h3>
                        <p className="person-email">
                          <Mail size={14} />
                          {user.email}
                        </p>
                        <div className="person-pills">
                          <span className={`event-status role-${user.role}`}>
                            <i />
                            {ROLE_LABELS[user.role] || user.role}
                          </span>
                          {spec && (
                            <span className="event-status spec-pill">
                              <SpecIcon size={12} />
                              {spec.label}
                            </span>
                          )}
                        </div>
                        <div className="person-foot">
                          <span>Joined {formatDate(user.created_at) || '—'}</span>
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="dashboard-empty">
                <UsersIcon size={28} />
                <h3>{hasFilters ? 'No matching users' : 'No users yet'}</h3>
                <p>
                  {hasFilters
                    ? 'Try a different search or clear your filters.'
                    : 'Add the first user to build your team.'}
                </p>
                {hasFilters ? (
                  <Button variant="outline" onClick={clearFilters}>
                    Clear filters
                  </Button>
                ) : (
                  <Button onClick={() => handleOpenDialog()}>Add user</Button>
                )}
              </div>
            )}

            <div className="table-footer">
              <span role="status" aria-live="polite">
                {filtered.length === users.length
                  ? `${users.length} users`
                  : `${filtered.length} of ${users.length} users`}
              </span>
            </div>
          </section>
        )}
      </div>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete user?"
        description={`Are you sure you want to delete user "${userToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleDeleteUser}
        confirmText="Delete user"
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-slate-900">
              {editingUser ? 'Edit user' : 'Add user'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                data-testid="user-name-input"
                placeholder="John Doe"
                className="mt-2"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                data-testid="user-email-input"
                type="email"
                placeholder="john@example.com"
                className="mt-2"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!!editingUser}
              />
              {editingUser && <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>}
            </div>

            {!editingUser && (
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  className="mt-2"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Role</Label>
                <Select
                  data-testid="user-role-select"
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="media_head">Media Head</SelectItem>
                    <SelectItem value="team_member">Team Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.role === 'team_member' && (
                <div>
                  <Label className="text-sm font-medium text-slate-700">Specialization</Label>
                  <Select
                    data-testid="user-specialization-select"
                    value={formData.specialization}
                    onValueChange={(value) => setFormData({ ...formData, specialization: value })}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="photo">Photography</SelectItem>
                      <SelectItem value="video">Videography</SelectItem>
                      <SelectItem value="editing">Editing</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>{editingUser ? 'Save changes' : 'Add user'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Users;
